import { fetchFieldLogSessions, fetchFieldLogVisit } from '@/features/field-log/api/fieldLog';
import {
  exportSupervisorSessions,
  fetchSupervisorCameras,
  fetchSupervisorMemberOptions,
  fetchSupervisorMembers,
  fetchSupervisorSummary,
} from '@/features/supervisor/api/supervisor';
import {
  SUPERVISOR_KEY,
  SUPERVISOR_MATCH_TOLERANCE,
  SUPERVISOR_PAGE_SIZE,
  SUPERVISOR_STALE_TIME,
} from '@/features/supervisor/config/constants';
import type {
  SupervisorActivitySessionFilters,
  SupervisorCamerasFilters,
  SupervisorTeamScope,
} from '@/features/supervisor/types/supervisor';
import {
  type QueryClient,
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';

const SUPERVISOR_FOOTAGE_REVIEW_QUERY_KEYS = [
  [SUPERVISOR_KEY, 'summary'],
  [SUPERVISOR_KEY, 'members'],
  [SUPERVISOR_KEY, 'cameras'],
  [SUPERVISOR_KEY, 'sessions'],
  [SUPERVISOR_KEY, 'visit'],
] as const;

/** Refreshes only Supervisor data whose rows or aggregates include footage review state. */
export function invalidateSupervisorFootageReviewQueries(queryClient: QueryClient) {
  for (const queryKey of SUPERVISOR_FOOTAGE_REVIEW_QUERY_KEYS) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

export function supervisorSummaryQuery(scope: SupervisorTeamScope) {
  return queryOptions({
    queryKey: [SUPERVISOR_KEY, 'summary', scope],
    queryFn: ({ signal }) => fetchSupervisorSummary(scope, { signal }),
    staleTime: SUPERVISOR_STALE_TIME,
  });
}

export function useSupervisorSummary(scope: SupervisorTeamScope) {
  return useQuery(supervisorSummaryQuery(scope));
}

export function useSuspenseSupervisorSummary(scope: SupervisorTeamScope) {
  return useSuspenseQuery(supervisorSummaryQuery(scope));
}

export function supervisorMembersQuery(scope: SupervisorTeamScope) {
  return infiniteQueryOptions({
    queryKey: [SUPERVISOR_KEY, 'members', scope],
    queryFn: ({ pageParam, signal }) => fetchSupervisorMembers(scope, pageParam, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    staleTime: SUPERVISOR_STALE_TIME,
  });
}

export function useSupervisorMembers(scope: SupervisorTeamScope) {
  return useInfiniteQuery(supervisorMembersQuery(scope));
}

export function useSupervisorMemberOptions(
  params: { organizationId?: readonly string[]; purpose: 'create' | 'filter'; q?: string },
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: [SUPERVISOR_KEY, 'member-options', params],
    queryFn: ({ pageParam, signal }) => fetchSupervisorMemberOptions(params, pageParam, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
  });
}

export function supervisorCamerasQuery(filters: SupervisorCamerasFilters, enabled = true) {
  return infiniteQueryOptions({
    queryKey: [SUPERVISOR_KEY, 'cameras', filters],
    queryFn: ({ pageParam, signal }) => fetchSupervisorCameras(filters, pageParam, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
    staleTime: SUPERVISOR_STALE_TIME,
  });
}

export function useSupervisorCameras(filters: SupervisorCamerasFilters, enabled = true) {
  return useInfiniteQuery(supervisorCamerasQuery(filters, enabled));
}

export function useSuspenseSupervisorCameras(filters: SupervisorCamerasFilters) {
  return useSuspenseInfiniteQuery(supervisorCamerasQuery(filters));
}

export function supervisorSessionsQuery(filters: SupervisorActivitySessionFilters) {
  return infiniteQueryOptions({
    queryKey: [SUPERVISOR_KEY, 'sessions', filters],
    queryFn: ({ pageParam, signal }) =>
      fetchFieldLogSessions(
        { ...filters, cursor: pageParam, limit: SUPERVISOR_PAGE_SIZE },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    staleTime: SUPERVISOR_STALE_TIME,
  });
}

export function useSupervisorSessions(filters: SupervisorActivitySessionFilters) {
  return useInfiniteQuery(supervisorSessionsQuery(filters));
}

export function useSuspenseSupervisorSessions(filters: SupervisorActivitySessionFilters) {
  return useSuspenseInfiniteQuery(supervisorSessionsQuery(filters));
}

export function useSupervisorSession(visitId: string, projectId?: string) {
  return useQuery({
    queryKey: [SUPERVISOR_KEY, 'visit', visitId, projectId],
    queryFn: ({ signal }) =>
      fetchFieldLogVisit(
        visitId as `VIS-${string}`,
        {
          include: ['recordingRange'],
          projectId,
          toleranceMinutes: SUPERVISOR_MATCH_TOLERANCE,
        },
        { signal },
      ),
    enabled: Boolean(visitId),
  });
}

export function useSupervisorExport() {
  return useMutation({
    mutationFn: (filters: SupervisorActivitySessionFilters) => exportSupervisorSessions(filters),
  });
}
